import PubSub from 'pubsub-js';
import { getEnvConfig, isCOChannel } from '@absis/shared';
import { AppBridgeOptions } from '../types/app-types';
import {
  TicketOptions,
  IParamsStore,
  EvictionTypes,
} from './params-store/commmon';
import { IndexedDBStore } from './params-store/indexeddb-store';
import { MemoryStore } from './params-store/memory-store';

declare global {
  interface Window {
    __GlobalOpenFrontPubSub__: PubSub;
  }
}

class AppBridge {
  private pubSubInstance: PubSub;
  private paramsStore: IParamsStore;
  private subscriptionTokens: Map<string, Set<string>> = new Map();
  public options: AppBridgeOptions;

  constructor(options?: AppBridgeOptions) {
    if (!window.top) throw new Error("Can't get window top");
    if (!window.top.__GlobalOpenFrontPubSub__) {
      window.top.__GlobalOpenFrontPubSub__ = PubSub;
    }
    this.pubSubInstance = window.top.__GlobalOpenFrontPubSub__;
    const envConfig = getEnvConfig(window.location.origin);
    if (!options) {
      options = {};
    }
    if (options.sharedTickets === undefined) {
      options.sharedTickets = !!(envConfig && isCOChannel(envConfig.channel));
    }
    this.paramsStore = this.createStore(options);
    this.options = options;
  }

  public subscribe(
    appName: string,
    action: string,
    subscriber: (message?: string, data?: unknown) => unknown
  ): string {
    const topic = this.calculateTopic(appName, action);
    const token = this.pubSubInstance.subscribe(topic, subscriber);
    this.addSubscriptionToken(topic, token);
    return token;
  }

  public unsubscribe(appName: string, action?: string, token?: string): void {
    const topic = this.calculateTopic(appName, action);
    this.deleteSubscriptionToken(topic, token);
  }

  public publish(appName: string, action: string, data: unknown): void {
    const topic = this.calculateTopic(appName, action);
    this.pubSubInstance.publish(topic, data);
  }

  public getSubscriptions(appName: string): string[] {
    const topic = this.calculateTopic(appName);
    return this.pubSubInstance.getSubscriptions(topic);
  }

  public countSubscriptions(appName: string, action?: string): number {
    const topic = this.calculateTopic(appName, action);
    return this.pubSubInstance.countSubscriptions(topic);
  }

  public ticket(data: unknown, options?: TicketOptions): Promise<string> {
    return this.paramsStore.ticket(data, options);
  }

  public ticketValue<T>(id: string): Promise<T | undefined> {
    return this.paramsStore.ticketValue(id);
  }

  public async removeTicket(id: string): Promise<void> {
    return this.paramsStore.removeTicket(id);
  }

  private calculateTopic(appName: string, action?: string): string {
    let topic = appName;
    if (action && action.trim() !== '') {
      topic += `.${action}`;
    }
    return topic;
  }

  private addSubscriptionToken(topic: string, token: string): void {
    let topicTokens: Set<string> | undefined =
      this.subscriptionTokens.get(topic);
    if (topicTokens) {
      topicTokens.add(token);
    } else {
      topicTokens = new Set<string>().add(token);
      this.subscriptionTokens.set(topic, topicTokens);
    }
  }

  private deleteSubscriptionToken(topic: string, token?: string): void {
    const regex = new RegExp(`^${topic}($|\..+)`, 'gm');
    this.subscriptionTokens.forEach((value, key) => {
      if (regex.test(key)) {
        if (token) {
          value.delete(token);
          this.pubSubInstance.unsubscribe(token);
        } else {
          this.subscriptionTokens.delete(key);
          value.forEach(tokenToDelete =>
            this.pubSubInstance.unsubscribe(tokenToDelete)
          );
          value.clear();
        }
      }
    });
  }

  private createStore(options: AppBridgeOptions): IParamsStore {
    return options.sharedTickets ? new IndexedDBStore() : new MemoryStore();
  }
}

export {
  AppBridge,
  AppBridgeOptions,
  TicketOptions,
  IParamsStore,
  EvictionTypes,
};
